'use client';

import { useGetMenuFormPermissions } from '@/api/permission';
import { logoutImpersonateUser, usePingUsers } from '@/api/user';
import Header from '@/components/common/Header';
import MainMenu from '@/components/common/MainMenu';
import Window from '@/components/common/Window';
import { useSocket } from '@/context/socketContext';
import { useToast } from '@/hooks/use-toast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { clearImpersonation } from '@/redux/slice/tokenSlice';
import { openWindow, switchToNextWindow, switchToPreviousWindow, updateWindowPosition } from '@/redux/slice/windowSlice';
import { RootState, useAppSelector } from '@/redux/store';
import { validateToken } from '@/utils/auth-helper';
import { getEnvironmentConfig } from '@/utils/common/environment';
import { ACCESS_TOKEN_MAX_AGE } from '@/utils/constant';
import { getCookie, setCookie } from '@/utils/cookies';
import { SocketResponse } from '@/utils/types/common';
import { SocketUser, WindowData } from '@/utils/types/window';
import clsx from 'clsx';
import { User } from 'lucide-react';
import { signIn, signOut } from 'next-auth/react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
const {
  backgroundImage
} = getEnvironmentConfig();
export default function DefaultLayout({
  children
}: Readonly<{
  children: ReactNode;
}>) {
  const dispatch = useDispatch();
  const {
    toast
  } = useToast();
  const user = useCurrentUser();
  const {
    socket,
    joinModule,
    isConnected,
    leaveModule,
    leaveAllModules,
    swapRoleChannels
  } = useSocket();
  const windows = useSelector((state: RootState) => state.windows.windows);
  const focusedWindowId = useSelector((state: RootState) => state.windows.focusedWindowId);
  const isImpersonating = useAppSelector(state => state.token.isImpersonating);
  const originalUser = useAppSelector(state => state.token.originalUser);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  usePingUsers();
  const {
    refetch: refetchMenuPermissions
  } = useGetMenuFormPermissions();

  // Sort windows by id to ensure stable rendering order in DOM
  const sortedWindows = useMemo(() => [...windows].sort((a, b) => (a.id || 0) - (b.id || 0)), [windows]);

  // Map each window to a z-index based on its sequenceIndex to maintain visual stacking order
  const windowZIndexes = useMemo(() => {
    const mruSorted = [...windows].sort((a, b) => (a.sequenceIndex || 0) - (b.sequenceIndex || 0));
    const mapping: Record<number, number> = {};
    mruSorted.forEach((win, index) => {
      mapping[win.id] = 30 + index;
    });
    return mapping;
  }, [windows]);
  const updateHeights = useCallback(() => {
    if (headerRef.current && footerRef.current) {
      const newHeaderHeight = headerRef.current.getBoundingClientRect().height;
      const newFooterHeight = footerRef.current.getBoundingClientRect().height;

      // Only update if values actually changed to avoid unnecessary re-renders
      setHeaderHeight(prev => {
        const rounded = Math.round(newHeaderHeight);
        return prev !== rounded ? rounded : prev;
      });
      setFooterHeight(prev => {
        const rounded = Math.round(newFooterHeight);
        return prev !== rounded ? rounded : prev;
      });
    }
  }, []);
  const menuPermissionChannels = useMemo(() => {
    const roles = user?.roles?.length ? user.roles : user?.role ? [user.role] : [];
    return roles.map(r => `menu-permission-${r.toLowerCase().replace(/\s+/g, '-')}`);
  }, [user?.roles, user?.role]);
  useEffect(() => {
    if (!socket || !isConnected) return;
    joinModule('logoutUser', user as SocketUser);
    menuPermissionChannels.forEach(channel => joinModule(channel, user as SocketUser));
    const handleUpdateEvent = async (data: SocketResponse) => {
      if (user?.employeeId == data?.data?.employeeId) {
        const callbackUrl = process.env.NEXT_PUBLIC_APP_URL ?? 'https://star.trsrentelco.com';
        localStorage.removeItem('firstLoginWindowShown');
        localStorage.removeItem('persist:root');
        document.cookie.split(';').forEach(cookie => {
          const cookieName = cookie.split('=')[0].trim();
          const domain = window.location.hostname;
          const path = '/';
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
        });
        leaveAllModules(user as SocketUser);
        signOut({
          callbackUrl: `${callbackUrl}/login`,
          redirect: true
        });
      }
    };
    const handleMenuPermissionUpdate = async () => {
      refetchMenuPermissions();
    };
    socket.on('logoutUser', handleUpdateEvent);
    menuPermissionChannels.forEach(channel => socket.on(channel, handleMenuPermissionUpdate));
    return () => {
      socket.off('logoutUser', handleUpdateEvent);
      menuPermissionChannels.forEach(channel => {
        socket.off(channel, handleMenuPermissionUpdate);
        leaveModule(channel, user as SocketUser);
      });
      leaveModule('logoutUser', user as SocketUser);
    };
  }, [socket, isConnected, menuPermissionChannels, refetchMenuPermissions, user]);
  const handleLogoutImpersonation = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      const currentToken = await getCookie('access-token');
      if (!currentToken) {
        toast({
          title: 'Failed',
          description: 'No active session found',
          variant: 'destructive'
        });
        return;
      }

      // Call impersonation API
      const impersonateResponse = await logoutImpersonateUser();
      if (!impersonateResponse.status || !impersonateResponse.data) {
        toast({
          title: 'Failed',
          description: impersonateResponse.message || 'Failed to impersonate user',
          variant: 'destructive'
        });
        return;
      }
      const impersonationToken = impersonateResponse.data;

      // Validate the new token and get user data
      const validationResult = await validateToken(impersonationToken);
      if (!validationResult) {
        toast({
          title: 'Failed',
          description: 'Invalid impersonation token',
          variant: 'destructive'
        });
        return;
      }
      await setCookie('access-token', impersonationToken, {
        path: '/',
        maxAge: ACCESS_TOKEN_MAX_AGE
      });

      //Re-authenticate with NextAuth using the new token
      const decodedImpersonationToken = decodeURIComponent(impersonationToken);
      const signInResult = await signIn('credentials', {
        accessToken: decodedImpersonationToken,
        redirect: false,
        callbackUrl: '/login'
      });
      if (signInResult?.error) {
        // If sign-in fails, restore original token
        toast({
          title: 'Failed',
          description: 'Failed to update session',
          variant: 'destructive'
        });
        return;
      }
      const originalRoleChannels = (originalUser?.roles?.length ? originalUser.roles : originalUser?.role ? [originalUser.role] : []).map(r => `menu-permission-${r.toLowerCase().replace(/\s+/g, '-')}`);
      dispatch(clearImpersonation());
      swapRoleChannels(menuPermissionChannels, originalRoleChannels, user as SocketUser);
    } catch (error) {
      console.error('Impersonation error:', error);
      toast({
        title: 'Failed',
        description: 'Could not activate impersonation',
        variant: 'destructive'
      });
    } finally {
      setIsLoggingOut(false);
    }
  }, [dispatch, toast, user, originalUser, menuPermissionChannels, swapRoleChannels]);
  useEffect(() => {
    if (isClient) {
      updateHeights();
    }
    window.addEventListener('resize', updateHeights);
    return () => {
      window.removeEventListener('resize', updateHeights);
    };
  }, [isClient, updateHeights]);

  // Recalculate heights when windows maximize/minimize state changes
  useEffect(() => {
    if (isClient) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        updateHeights();
      });
    }
  }, [isClient, windows.length, sortedWindows.map(w => w.isMaximized).join(','), updateHeights]);

  // Recalculate heights when impersonation banner appears/disappears
  useEffect(() => {
    if (isClient) {
      requestAnimationFrame(() => {
        updateHeights();
      });
    }
  }, [isClient, isImpersonating, updateHeights]);

  // Additional safeguard: recalculate on window focus change
  useEffect(() => {
    if (isClient && focusedWindowId !== null) {
      requestAnimationFrame(() => {
        updateHeights();
      });
    }
  }, [isClient, focusedWindowId, updateHeights]);

  // Shift + re-clamp all open window positions when header/footer height changes.
  // Tracks the previous headerHeight so windows move up when the impersonation
  // banner disappears (delta < 0) and move down when it appears (delta > 0).
  const windowsRef = useRef(windows);
  useEffect(() => {
    windowsRef.current = windows;
  }, [windows]);
  const prevHeaderHeightRef = useRef(0);
  useEffect(() => {
    if (!isClient || !headerHeight) return;
    const deltaY = headerHeight - prevHeaderHeightRef.current;
    prevHeaderHeightRef.current = headerHeight;
    if (deltaY === 0) return;
    windowsRef.current.forEach(win => {
      if (win.isMaximized || win.isMinimized || !win.isWindowOpen) return;
      if (win.position.x === 0 && win.position.y === 0) return;
      const winWidth = win.size?.width ?? 840;
      const winHeightPx = win.size?.height ?? 400;
      const shiftedY = win.position.y + deltaY;
      const maxY = Math.max(headerHeight, window.innerHeight - winHeightPx - footerHeight);
      const clampedY = Math.max(headerHeight, Math.min(shiftedY, maxY));
      const maxX = Math.max(0, window.innerWidth - winWidth);
      const clampedX = Math.max(0, Math.min(win.position.x, maxX));
      if (clampedY !== win.position.y || clampedX !== win.position.x) {
        dispatch(updateWindowPosition({
          id: win.id,
          position: {
            x: clampedX,
            y: clampedY
          }
        }));
      }
    });
  }, [headerHeight, footerHeight, isClient, dispatch]);
  useEffect(() => {
    setIsClient(true);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.code === 'KeyS') {
        e.preventDefault();
        dispatch(switchToPreviousWindow());
      } else if (e.altKey && e.code === 'KeyA') {
        e.preventDefault();
        dispatch(switchToNextWindow());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  useEffect(() => {
    if (!isClient) return;
    const hasOpenedOnce = localStorage.getItem('firstLoginWindowShown');
    if (!hasOpenedOnce) {
      dispatch(openWindow({
        title: 'Home',
        componentKey: 'home',
        id: null
      }));
      localStorage.setItem('firstLoginWindowShown', 'true');
    }
  }, [isClient, dispatch]);
  if (!isClient) {
    return null;
  }
  return <div data-sentry-component="DefaultLayout" data-sentry-source-file="DefaultLayout.tsx">
      {/* <!-- ===== Main Content Start ===== --> */}
      <main>
        <div className="h-screen w-full bg-cover bg-no-repeat" style={{
        backgroundImage
      }}>
          <div ref={headerRef} style={{
          position: 'relative',
          zIndex: 9999
        }}>
            {isImpersonating && <div className={clsx('flex w-full items-center justify-center p-1 text-[12px] text-white md:text-[13px]', isLoggingOut && 'animate-moving-stripes')} style={{
            backgroundImage: `
                    repeating-linear-gradient(
                      45deg,
                      #2f2f2f,
                      #2f2f2f 10px,
                      #3a3a3a 10px,
                      #3a3a3a 20px
                    )
                  `,
            backgroundSize: isLoggingOut ? '200% 200%' : '100% 100%'
          }}>
                <div className="text-center leading-[1.3]">
                  {isLoggingOut ? <span>Logging out...</span> : <div className="flex items-center justify-center gap-1">
                      <User className="size-4" />
                      <div>
                        Logged in as {user?.employee?.empFirstName} {user?.employee?.empLastName} ({user?.email}) |{' '}
                        <button type="button" className="inline cursor-pointer border-none bg-transparent p-0 text-inherit underline" onClick={handleLogoutImpersonation}>
                          Log out as {user?.employee?.empFirstName} {user?.employee?.empLastName}
                        </button>
                      </div>
                    </div>}
                </div>
              </div>}
            <Header data-sentry-element="Header" data-sentry-source-file="DefaultLayout.tsx" />
          </div>
          <div className="mx-auto max-w-screen-2xl p-4 md:p-6 2xl:p-10">{children}</div>
          <div ref={footerRef} style={{
          position: 'relative',
          zIndex: 9999
        }}>
            <MainMenu data-sentry-element="MainMenu" data-sentry-source-file="DefaultLayout.tsx" />
          </div>
        </div>
      </main>
      {/* <!-- ===== Main Content End ===== --> */}

      {/* <!-- ===== Window Start ===== --> */}
      {sortedWindows?.map((window: WindowData) => <div className={clsx(window?.disableOtherInteractions ? 'fixed inset-0 z-[9999] flex items-center justify-center' : '')} key={window.id}>
          {window?.disableOtherInteractions && <div className="pointer-events-auto absolute inset-0" />}
          <Window id={window.id} title={window.title} componentKey={window.componentKey} isMinimized={window.isMinimized} isMaximized={window.isMaximized} isWindowOpen={window.isWindowOpen} isFocused={focusedWindowId === window.id} headerHeight={headerHeight} footerHeight={footerHeight} winData={window?.winData} disableClose={window?.disableClose} disableMessage={window?.disableMessage} zIndex={windowZIndexes[window.id]} />
        </div>) ?? null}
      {/* <!-- ===== Window End ===== --> */}
    </div>;
}